import React, {useState} from 'react';
import {FlatList, Pressable, ScrollView, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {useClubLeaderboard} from '@/Hooks/ClubsApi/useClubsQueries';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {LeaderboardRow} from '@/Hooks/ClubsApi/types';
import {LeaderboardSkeleton} from './ClubRoomSkeletons';

// Top-3 medal tints (design leaderboard VM: ['#FACF47','#CACACA','#E0A96E']).
const rankColor = (r: number) =>
  r === 1 ? '#D1AB41' : r === 2 ? '#A8A29A' : r === 3 ? '#B5794A' : '#888280';

const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#67B3E1', '#937ADB', '#D1AB41'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];

export default function LeaderboardTab({
  clubId,
  variant = 'mobile',
  onOpenStandings,
}: {
  clubId: number;
  variant?: 'mobile' | 'rail';
  onOpenStandings?: () => void;
}) {
  const [period, setPeriod] = useState<'daily' | 'weekly'>('daily');
  const {data, isLoading} = useClubLeaderboard(clubId, period);
  const myId = useLoggedInUserStore(s => s.userId);
  const isRail = variant === 'rail';

  const Toggle = () => (
    <View
      style={
        isRail
          ? {
              flexDirection: 'row',
              backgroundColor: '#EDE6D2',
              borderRadius: 8,
              padding: 3,
            }
          : {flexDirection: 'row', gap: 6}
      }>
      {(['daily', 'weekly'] as const).map(p => {
        const active = period === p;
        if (isRail) {
          // Segmented control inside the rail card header (design line 885).
          return (
            <Pressable
              key={p}
              testID={`lb-period-${p}`}
              onPress={() => setPeriod(p)}
              style={{
                paddingVertical: 5,
                paddingHorizontal: 12,
                borderRadius: 6,
                backgroundColor: active ? '#3DAB9E' : 'transparent',
              }}>
              <CCText
                style={{
                  fontWeight: '600',
                  color: active ? '#fff' : '#6B584D',
                  fontSize: 12,
                }}>
                {p === 'daily' ? 'Today' : 'Week'}
              </CCText>
            </Pressable>
          );
        }
        // Mobile pill row (design line 411).
        return (
          <Pressable
            key={p}
            testID={`lb-period-${p}`}
            onPress={() => setPeriod(p)}
            style={{
              paddingVertical: 7,
              paddingHorizontal: 14,
              borderRadius: 999,
              backgroundColor: active ? '#3DAB9E' : '#fff',
              borderWidth: active ? 0 : 1,
              borderColor: 'rgba(38,35,34,0.12)',
            }}>
            <CCText
              style={{
                fontWeight: '600',
                color: active ? '#fff' : '#6B584D',
                fontSize: 13,
              }}>
              {p === 'daily' ? 'Today' : 'This Week'}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );

  const renderRow = (item: LeaderboardRow) => {
    console.log('LeaderboardRow', item, 'myId', myId);
    const isMe = String(item.user_id) === String(myId);
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: isRail ? 9 : 11,
          paddingVertical: isRail ? 7 : 8,
          paddingHorizontal: isRail ? 9 : 11,
          borderRadius: 12,
          // Me-row highlight (design: #FDF1C8 bg + 1.5px #FACF47 border).
          backgroundColor: isMe ? '#FDF1C8' : '#fff',
          borderWidth: isMe ? 1.5 : 0,
          borderColor: isMe ? '#FACF47' : 'transparent',
          ...(isMe
            ? {}
            : {
                shadowColor: '#4D3F37',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: 0.05,
                shadowRadius: 2,
                elevation: 1,
              }),
        }}>
        <CCText
          style={{
            width: isRail ? 26 : 26,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: '700',
            color: rankColor(item.rank),
          }}>
          {item.rank}
        </CCText>
        <View
          style={{
            width: isRail ? 26 : 32,
            height: isRail ? 26 : 32,
            borderRadius: 16,
            backgroundColor: avatarColor(item.username),
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <CCText
            style={{
              color: '#fff',
              fontWeight: '600',
              fontSize: isRail ? 10.5 : 12,
            }}>
            {item.username.charAt(0).toUpperCase()}
          </CCText>
        </View>
        <View style={{flex: 1}}>
          <CCText
            numberOfLines={1}
            style={{
              fontSize: isRail ? 13 : 14.5,
              fontWeight: isMe ? '700' : '600',
              color: '#262322',
            }}>
            {isMe ? 'You' : item.name || item.username}
          </CCText>
          {/* Username on the second line (smaller). "You" always keeps its real
              username below; others show it only when a name is present. */}
          {isMe || item.name ? (
            <CCText
              numberOfLines={1}
              style={{
                fontSize: isRail ? 10.5 : 11.5,
                color: '#888280',
                marginTop: 1,
              }}>
              {item.username}
            </CCText>
          ) : null}
        </View>
        <CCText
          style={{
            fontSize: isRail ? 13 : 14,
            fontWeight: '700',
            color: '#4D3F37',
          }}>
          {item.points}
          {isRail ? null : (
            <CCText style={{fontSize: 11, fontWeight: '500', color: '#888280'}}>
              {' '}
              pts
            </CCText>
          )}
        </CCText>
      </View>
    );
  };

  if (isRail) {
    // Rail: header row (title + toggle) then a plain (non-scrolling) column.
    return (
      <View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}>
          <CCText style={{fontSize: 13, fontWeight: '700', color: '#262322'}}>
            Leaderboard
          </CCText>
          <Toggle />
        </View>
        {isLoading ? (
          <LeaderboardSkeleton variant="rail" />
        ) : (
          // Show ~10 rows then scroll inside. maxHeight is sized so the 10th row is
          // partially cut, hinting there's more to scroll. (rail row ≈ 40px + 5 gap.)
          <ScrollView
            style={{maxHeight: 430}}
            contentContainerStyle={{gap: 5}}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled>
            {(data?.rows ?? []).map(item => (
              <View key={String(item.user_id)}>{renderRow(item)}</View>
            ))}
          </ScrollView>
        )}
        {onOpenStandings ? (
          <Pressable
            testID="open-standings"
            onPress={onOpenStandings}
            style={{paddingTop: 10, alignSelf: 'flex-end'}}>
            <CCText
              style={{fontSize: 12.5, fontWeight: '600', color: '#3DAB9E'}}>
              Standings →
            </CCText>
          </Pressable>
        ) : null}
      </View>
    );
  }

  return (
    <View style={{flex: 1}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 14,
          paddingTop: 12,
          paddingBottom: 6,
        }}>
        <Toggle />
        <View style={{flex: 1}} />
        {onOpenStandings ? (
          <Pressable testID="open-standings" onPress={onOpenStandings}>
            <CCText
              style={{fontSize: 12.5, fontWeight: '600', color: '#3DAB9E'}}>
              Standings →
            </CCText>
          </Pressable>
        ) : null}
      </View>
      {isLoading ? (
        <LeaderboardSkeleton />
      ) : (
        <FlatList
          data={data?.rows ?? []}
          keyExtractor={r => String(r.user_id)}
          contentContainerStyle={{paddingHorizontal: 14, paddingTop: 4, gap: 6}}
          renderItem={({item}) => renderRow(item)}
        />
      )}
    </View>
  );
}
